import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type SkillRow = Database["public"]["Tables"]["skill_registry"]["Row"];
export type SkillUpdate = Database["public"]["Tables"]["skill_registry"]["Update"];
export type SkillInsert = Database["public"]["Tables"]["skill_registry"]["Insert"];
export type SkillFeedback = Database["public"]["Tables"]["skill_feedback"]["Row"];
export type SkillABTest = Database["public"]["Tables"]["skill_ab_tests"]["Row"];
export type ModelDecision = Database["public"]["Tables"]["model_decisions"]["Row"];
export type ResponseQualityScore = Database["public"]["Tables"]["response_quality_scores"]["Row"];
export type RoutingRule = Database["public"]["Tables"]["skill_routing_rules"]["Row"];
export type ResponsePolicy = Database["public"]["Tables"]["response_policies"]["Row"];
export type IntentEvent = Database["public"]["Tables"]["intent_events"]["Row"];
export type SkillQualityHistory = Database["public"]["Tables"]["skill_quality_history"]["Row"];

export interface SkillStats {
  name: string;
  count: number;
  avgMs: number;
  avgQuality: number;
}

const ACTIVE_ORG_KEY = "syra.activeOrgId";

export interface OrgOption {
  org_id: string;
  name: string;
  role: string | null;
  active_groups: number;
}

/** Set active org and invalidate dependent queries (call after user picks). */
export function setActiveOrgId(orgId: string) {
  try { localStorage.setItem(ACTIVE_ORG_KEY, orgId); } catch {}
}

/** List orgs the current user belongs to, sorted by activity (active groups desc, role priority). */
export function useUserOrgs() {
  return useQuery({
    queryKey: ["skill-os", "user-orgs"],
    queryFn: async (): Promise<OrgOption[]> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      const { data: memberships } = await supabase
        .from("org_members")
        .select("org_id, role, organizations(name)")
        .eq("user_id", user.id);
      if (!memberships?.length) return [];
      const ids = memberships.map((m: any) => m.org_id);
      // Count active groups per org
      const { data: groups } = await supabase
        .from("syra_groups")
        .select("org_id, active")
        .in("org_id", ids);
      const counts = new Map<string, number>();
      for (const g of (groups as any[]) ?? []) {
        if (g.active) counts.set(g.org_id, (counts.get(g.org_id) ?? 0) + 1);
      }
      const rolePriority: Record<string, number> = { owner: 3, admin: 2, member: 1 };
      return memberships
        .map((m: any) => ({
          org_id: m.org_id,
          name: m.organizations?.name ?? m.org_id.slice(0, 8),
          role: m.role ?? null,
          active_groups: counts.get(m.org_id) ?? 0,
        }))
        .sort((a, b) =>
          b.active_groups - a.active_groups ||
          (rolePriority[b.role ?? ""] ?? 0) - (rolePriority[a.role ?? ""] ?? 0) ||
          a.name.localeCompare(b.name),
        );
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Current active org id — picks from localStorage, falls back to most-active org. */
export function useCurrentOrgId() {
  return useQuery({
    queryKey: ["skill-os", "current-org"],
    queryFn: async (): Promise<string | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      const { data: memberships } = await supabase
        .from("org_members")
        .select("org_id, role")
        .eq("user_id", user.id);
      if (!memberships?.length) return null;
      const memberIds = new Set(memberships.map((m: any) => m.org_id));

      // 1. Prefer localStorage if still a valid membership
      try {
        const stored = localStorage.getItem(ACTIVE_ORG_KEY);
        if (stored && memberIds.has(stored)) return stored;
      } catch {}

      // 2. Prefer org with most active syra_groups
      const ids = Array.from(memberIds);
      const { data: groups } = await supabase
        .from("syra_groups")
        .select("org_id, active")
        .in("org_id", ids);
      const counts = new Map<string, number>();
      for (const g of (groups as any[]) ?? []) {
        if (g.active) counts.set(g.org_id, (counts.get(g.org_id) ?? 0) + 1);
      }
      let best = ids[0];
      let bestCount = counts.get(best) ?? 0;
      for (const id of ids) {
        const c = counts.get(id) ?? 0;
        if (c > bestCount) { best = id; bestCount = c; }
      }
      if (bestCount > 0) {
        try { localStorage.setItem(ACTIVE_ORG_KEY, best); } catch {}
        return best;
      }

      // 3. Fallback: owner/admin role
      const rolePriority: Record<string, number> = { owner: 3, admin: 2, member: 1 };
      const sorted = [...memberships].sort(
        (a: any, b: any) => (rolePriority[b.role ?? ""] ?? 0) - (rolePriority[a.role ?? ""] ?? 0),
      );
      const fallback = sorted[0]?.org_id ?? null;
      if (fallback) { try { localStorage.setItem(ACTIVE_ORG_KEY, fallback); } catch {} }
      return fallback;
    },
    staleTime: 10 * 60 * 1000,
  });
}

/** 1. List skills (registry) — includes global skills (org_id NULL) */
export function useSkills() {
  const { data: orgId } = useCurrentOrgId();
  return useQuery({
    queryKey: ["skill-os", "skills", orgId ?? "no-org"],
    enabled: orgId !== undefined,
    queryFn: async (): Promise<SkillRow[]> => {
      let q = supabase.from("skill_registry").select("*").is("archived_at", null);
      if (orgId) q = q.or(`org_id.eq.${orgId},org_id.is.null`);
      else q = q.is("org_id", null);
      const { data, error } = await q.order("priority", { ascending: false }).order("name");
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** 2. Toggle skill active flag */
export function useToggleSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("skill_registry").update({ active }).eq("id", id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skill-os", "skills"] }),
  });
}

/** 3. Aggregated feedback stats per skill (production only — excludes is_test) */
export function useSkillFeedbackStats() {
  const { data: orgId } = useCurrentOrgId();
  return useQuery({
    queryKey: ["skill-os", "feedback-stats", orgId ?? "no-org"],
    enabled: !!orgId,
    queryFn: async (): Promise<SkillStats[]> => {
      const { data, error } = await supabase
        .from("skill_feedback")
        .select("skill_name, execution_time_ms, quality_score")
        .eq("org_id", orgId!)
        .eq("is_test", false)
        .order("created_at", { ascending: false })
        .limit(2000);
      if (error) throw new Error(error.message);
      const map = new Map<string, { count: number; totalMs: number; totalQ: number; qCount: number }>();
      for (const f of data ?? []) {
        const cur = map.get(f.skill_name) ?? { count: 0, totalMs: 0, totalQ: 0, qCount: 0 };
        cur.count++;
        cur.totalMs += f.execution_time_ms ?? 0;
        if (typeof f.quality_score === "number") {
          cur.totalQ += f.quality_score;
          cur.qCount++;
        }
        map.set(f.skill_name, cur);
      }
      return Array.from(map.entries()).map(([name, s]) => ({
        name, count: s.count,
        avgMs: s.count ? s.totalMs / s.count : 0,
        avgQuality: s.qCount ? s.totalQ / s.qCount : 0,
      }));
    },
  });
}

/** 4. List A/B tests (org-scoped) */
export function useABTests() {
  const { data: orgId } = useCurrentOrgId();
  return useQuery({
    queryKey: ["skill-os", "ab-tests", orgId ?? "no-org"],
    enabled: !!orgId,
    queryFn: async (): Promise<SkillABTest[]> => {
      const { data, error } = await supabase
        .from("skill_ab_tests")
        .select("*")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** 5. Create A/B test */
export function useCreateABTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      skill_name: string;
      org_id: string;
      variant_a_prompt: string;
      variant_b_prompt: string;
      variant_a_name?: string;
      variant_b_name?: string;
    }) => {
      const { error } = await supabase.from("skill_ab_tests").insert(payload);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skill-os", "ab-tests"] }),
  });
}

export interface PipelineExecution {
  id: string;
  created_at: string;
  skill_name: string;
  intent: string | null;
  intent_confidence: number | null;
  execution_time_ms: number | null;
  quality_score: number | null;
  was_sent_to_group: boolean | null;
  output_length: number | null;
  model_used: string | null;
  group_id: string | null;
  member_id: string | null;
  ab_variant: string | null;
  decision_id: string | null;
  context_metadata: unknown;
  memory_used_count: number | null;
  follow_up_messages: number | null;
  admin_reaction: string | null;
}

/** 6. Pipeline executions feed (org-scoped, excludes test runs) */
export function usePipelineFeed(filters: {
  skill?: string;
  intent?: string;
  groupId?: string;
  limit?: number;
} = {}) {
  const { data: orgId } = useCurrentOrgId();
  return useQuery({
    queryKey: ["skill-os", "pipeline-feed", orgId ?? "no-org", filters],
    enabled: !!orgId,
    queryFn: async (): Promise<PipelineExecution[]> => {
      let q = supabase
        .from("skill_feedback")
        .select(
          "id, created_at, skill_name, intent, intent_confidence, execution_time_ms, quality_score, was_sent_to_group, output_length, model_used, group_id, member_id, ab_variant, decision_id, context_metadata, memory_used_count, follow_up_messages, admin_reaction"
        )
        .eq("org_id", orgId!)
        .eq("is_test", false)
        .order("created_at", { ascending: false })
        .limit(filters.limit ?? 100);
      if (filters.skill) q = q.eq("skill_name", filters.skill);
      if (filters.intent) q = q.eq("intent", filters.intent);
      if (filters.groupId) q = q.eq("group_id", filters.groupId);
      const { data, error } = await q;
      if (error) throw new Error(error.message);
      return (data ?? []) as PipelineExecution[];
    },
    refetchInterval: 15_000,
  });
}

/** 7. Routing rules (org-scoped) */
export function useRoutingRules() {
  const { data: orgId } = useCurrentOrgId();
  return useQuery({
    queryKey: ["skill-os", "routing-rules", orgId ?? "no-org"],
    enabled: !!orgId,
    queryFn: async (): Promise<RoutingRule[]> => {
      const { data, error } = await supabase
        .from("skill_routing_rules")
        .select("*")
        .eq("org_id", orgId!)
        .order("typology")
        .order("intent");
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** 8. Response policies (org-scoped) */
export function useResponsePolicies() {
  const { data: orgId } = useCurrentOrgId();
  return useQuery({
    queryKey: ["skill-os", "response-policies", orgId ?? "no-org"],
    enabled: !!orgId,
    queryFn: async (): Promise<ResponsePolicy[]> => {
      const { data, error } = await supabase
        .from("response_policies")
        .select("*")
        .eq("org_id", orgId!)
        .order("typology");
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** 9. Submit intent correction (operator feedback) */
export function useCorrectIntent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ org_id, message_id, original_intent, corrected_intent }: {
      org_id: string;
      message_id: string | null;
      original_intent: string;
      corrected_intent: string;
    }) => {
      const { error } = await supabase.from("intent_events").insert({
        org_id, message_id, intent: original_intent, corrected_intent,
        was_correct: false, feedback_source: "operator",
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["intent-events"] }),
  });
}

/** 10. Quality history (temporal series per skill) */
export function useSkillQualityHistory(days = 30) {
  const { data: orgId } = useCurrentOrgId();
  return useQuery({
    queryKey: ["skill-os", "quality-history", orgId ?? "no-org", days],
    enabled: !!orgId,
    queryFn: async (): Promise<SkillQualityHistory[]> => {
      const since = new Date(Date.now() - days * 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("skill_quality_history")
        .select("*")
        .eq("org_id", orgId!)
        .gte("computed_at", since)
        .order("computed_at", { ascending: true });
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

/** 11. Test a skill (dry run via skill-executor with is_test flag) */
export function useTestSkill() {
  return useMutation({
    mutationFn: async ({ skill_name, message, org_id, group_id }: {
      skill_name: string;
      message: string;
      org_id: string;
      group_id?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke("skill-executor", {
        body: {
          skill_name,
          context: { message, org_id },
          org_id,
          group_id,
          test_mode: true,
        },
      });
      if (error) throw new Error(error.message);
      return data as { ok: boolean; skill: string; output: string; execution_ms: number; dispatch_type: string };
    },
  });
}
